//Enums:
import { globals } from 'config/globals';
import { ParticleType, ParticleFieldType, HomeParticleStateType } from 'app/enum/Particles';
import type { DepartmentType } from 'app/data/Enums';
//Contracts:
import { particles } from 'app/contracts/Particles';
//Particle libraries:
import { Particle } from 'particles/Particle';
import { ParticleField } from 'particles/ParticleField';
import { ParticleManager } from 'particles/ParticleManager';
import { HomeParticle } from 'app/views/home/webgl/particles/fields/HomeParticle';
import { SpacesGridParticle } from 'app/views/home/webgl/particles/fields/SpacesGridParticle';
import { ScreensGridParticle } from 'app/views/home/webgl/particles/fields/ScreensGridParticle';

import { HomeField } from 'app/views/home/webgl/particles/fields/HomeField';
import { ScreensGridField } from 'app/views/home/webgl/particles/fields/ScreensGridField';
import { SpacesGridField } from 'app/views/home/webgl/particles/fields/SpacesGridField';
import type { HomeWebGLDeps } from 'app/views/home/webgl/webgl.types';
import ParticleBehaviourLookup from 'particles/ParticleBehaviourLookup';

export class HomeParticleManager {
	public onParticlesReady: CallableFunction;

	private _container: PIXI.Container;
	private _sceneProps: particles.ParticlesSceneProps;
	private _particleManager: ParticleManager;
	private _homeField: HomeField;
	private _spacesPerspectiveGridField: SpacesGridField;
	private _pixelData: any;
	private _state: HomeParticleStateType;
	//Host callbacks threaded down from ParticlesWebGL (HomeField reads
	//getVirtualWidth, DepartmentColours calls the colourise* items):
	private _webglDeps: HomeWebGLDeps;
	private _particleConfig: particles.ParticleSetConfig;

	constructor(
		sceneProps: particles.ParticlesSceneProps,
		container: PIXI.Container,
		config: particles.ParticleSetConfig,
		webglDeps: HomeWebGLDeps
	) {
		this._sceneProps = sceneProps;
		this._container = container;

		this._particleManager = new ParticleManager(this._sceneProps, this._container);

		this._webglDeps = webglDeps;
		this._particleConfig = config;

		if (this._pixelData) {
			this.initParticles();
		}
	}

	private initParticles() {
		this._createParticles(this._particleConfig);

		setTimeout(() => {
			//The children have been added so sort the depth (A value of depth will have beenn forced on the particle)
			this._particleManager.sortDepth();
			this._homeField = this._particleManager.getFieldById('home-particle-field') as any;
			this._spacesPerspectiveGridField = this._particleManager.getFieldById('spaces-particle-field') as any;
			this._homeField.webglDeps = this._webglDeps;
			setTimeout(() => this.triggerOnReady(), 100);
		}, 1000);
	}

	private triggerOnReady() {
		if (this.onParticlesReady) {
			this.onParticlesReady();
		}
	}

	public get homeField(): HomeField {
		return this._homeField;
	}

	public colouriseSpacesScene(colour) {
		this._spacesPerspectiveGridField.colourise(colour);
	}

	public setIconPixelData(pixelData) {
		this._pixelData = pixelData;
		if (!this._homeField) {
			this.initParticles();
		}
	}

	//PUBLIC METHODS
	//---------------------------------------------------------------------------------------------
	public resize(): void {
		this._particleManager.resize();
	}

	public tick(): void {
		if (!globals.skipNormalParticleUpdates || ParticleBehaviourLookup.paused) {
			this._particleManager.tick();
		} else {
			this._homeField.updateSystemsGrid();
		}

		ParticleBehaviourLookup.tickActive();
	}

	public changeState(state: HomeParticleStateType, skipFieldJump: boolean = false): void {
		//Home field will manage it own complexities:
		this._state = state;
		this._homeField.changeState(state, skipFieldJump);
	}

	public get state(): HomeParticleStateType {
		return this._state;
	}

	public iconHover(): void {
		this._homeField.changeIconColourOnHover();
	}

	public setupForWork() {
		if (this._homeField) {
			this._homeField.hideAllButTransition();
		} else {
			console.warn('No this._homeField set in HomeParticleManager');
		}
	}

	public showFromWork(department: DepartmentType) {
		if (this._homeField) {
			this._homeField.showAllButTransition();
		} else {
			console.warn('No this._homeField set in HomeParticleManager');
		}
	}

	//PRIVATE METHODS
	//---------------------------------------------------------------------------------------------

	/** Add all the particles */
	private _createParticles(config: particles.ParticleSetConfig): void {
		if (config.particles) {
			config.particles.forEach((particleJSON, index) => this._createParticle(particleJSON, index));
		}
		if (config.fields) {
			config.fields.forEach((particleFieldJSON) => this._createField(particleFieldJSON));
		}
	}

	/** Add new particle via the particle manager */
	private _createParticle(config: particles.ParticleConfig, index: number): particles.Particle {
		let particle: particles.Particle;

		if (config.base.type === ParticleType.PARALLAX) {
		} else if (config.base.type === ParticleType.HOME) {
			let container = index > 100 && index < 100 + 262 ? this._sceneProps.iconContainer : null;

			particle = this._particleManager.addParticle(HomeParticle, config, container);
		} else if (config.base.type === ParticleType.SCREENS_GRID) {
			particle = this._particleManager.addParticle(ScreensGridParticle, config);
		} else if (config.base.type === ParticleType.SPACES_GRID) {
			particle = this._particleManager.addParticle(SpacesGridParticle, config);
		} else if (!config.base.type || config.base.type === ParticleType.SIMPLE) {
			particle = this._particleManager.addParticle(Particle, config);
		}

		return particle;
	}

	/** Add new field via the particle manager */
	private _createField(config: particles.ParticleFieldConfig): particles.ParticleField {
		let field: particles.ParticleField;

		if (config.type === ParticleFieldType.HOME) {
			field = this._particleManager.addField(HomeField, config);

			setTimeout(() => {
				(field as HomeField).setIconGrid(this._pixelData.icon);
				(field as HomeField).setPixelData(this._pixelData);
			}, 1000);
		} else if (config.type === ParticleFieldType.SCREENS_GRID) {
			field = this._particleManager.addField(ScreensGridField, config);
		} else if (config.type === ParticleFieldType.SPACES_GRID) {
			field = this._particleManager.addField(SpacesGridField, config);
		} else if (!config.type || config.type === ParticleFieldType.SIMPLE) {
			field = this._particleManager.addField(ParticleField, config);
		}

		return field;
	}
}
